import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Lock, Wand2, Type as TypeIcon, Captions } from 'lucide-react';
import { fmtTimePrecise } from './EditorClipTrimControls';
import { EditorVideoControls } from './EditorVideoControls';
import { EditorAudioControls } from './EditorAudioControls';
import { EditorKeyframeControls } from './EditorKeyframeControls';
import { EditorTextControls } from './EditorTextControls';
import { EditorCaptionControls } from './EditorCaptionControls';
import { EditorTransitionsPanel } from './EditorTransitionsPanel';
import { EditorEffectsPanel } from './EditorEffectsPanel';
import { EditorColorControls } from './EditorColorControls';

const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const ComingSoonRow = ({ icon: Icon, label }) => (
  <div className="flex items-center justify-between p-2.5 rounded-lg border border-white/5 bg-white/[0.02] opacity-60" data-testid={`inspector-coming-soon-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <span className="flex items-center gap-2 text-xs text-[#8A93A6]"><Icon className="h-3.5 w-3.5" />{label}</span>
    <span className="flex items-center gap-1 text-[10px] text-[#555E6E]"><Lock className="h-3 w-3" />Coming soon</span>
  </div>
);

/**
 * Area 3 — Inspector Panel. Stage 3B added real Video/Audio/Keyframe
 * controls. Stage 3C adds real Text & Title (EditorTextControls) OR
 * Caption-block (EditorCaptionControls) controls for text items — a
 * single text item switches between the two depending on whether
 * `text.segments` is populated — plus Transitions, Color/Filters and
 * Visual Effects/Privacy Blur for video items. Anything not genuinely
 * wired stays a visibly disabled "Coming soon" row, never fake-functional.
 */
export function EditorInspectorPanel({
  item, source, siblingSources, track, allTracks, localTime, masterComposition,
  onUpdateItem, onTransformChange, onCropChange, onSpeedChange, onTogglePip, onSetReframing,
  onAudioChange, onDetachAudio, onReplaceAudioSource, onAudioProcessingChange, onDuckingChange,
  onAddKeyframe, onRemoveKeyframe, onRequestTranscription,
  textStylePresets, showSafeZones, onToggleSafeZones, onDuplicateItem, onSaveTextPreset,
}) {
  if (!item) {
    return (
      <div className="glass rounded-xl p-4 h-full flex items-center justify-center" data-testid="editor-inspector-panel-empty">
        <p className="text-sm text-[#8A93A6] text-center">Select a clip on the timeline to edit its properties.</p>
      </div>
    );
  }

  const text = item.text || { text: '', font_family: 'Inter', font_size: 32, color: '#FFFFFF' };
  const setText = (partial) => {
    // Any manual style edit (as opposed to content/segments/transcription
    // bookkeeping) marks this item as no longer safe to silently refresh
    // from a "Generate Brand Presets" sync — matches the override
    // hierarchy's "never silently overwrite an explicit override" rule.
    const isStyleChange = !('segments' in partial) && !('transcription' in partial) && !('linked_source_id' in partial) && !('animated_caption_style' in partial) && !('subtitle_source_format' in partial);
    onUpdateItem(item.id, { text: { ...text, ...partial, ...(isStyleChange ? { brand_inherited: false } : {}) } });
  };
  const isCaptionBlock = (text.segments || []).length > 0;

  const toggleTextMode = () => {
    if (isCaptionBlock) {
      setText({ segments: [] });
    } else {
      setText({ segments: [{ id: newId(), start_seconds: 0, end_seconds: Math.min(item.duration_seconds, 2), text: text.text || 'New caption', words: [] }] });
    }
  };

  const applyPreset = (presetId) => {
    const preset = (textStylePresets || []).find((p) => p.id === presetId);
    if (!preset) return;
    const { id: _pid, name: _pname, ...styleFields } = preset;
    setText({ ...styleFields, style_preset_id: preset.id });
  };

  return (
    <div className="glass rounded-xl p-4 h-full overflow-y-auto space-y-5" data-testid="editor-inspector-panel">
      <div>
        <p className="text-xs text-[#555E6E] uppercase tracking-wide mb-1">Clip Source</p>
        <p className="text-sm text-white truncate" data-testid="inspector-source-title">{source?.title || item.content_source_id || (item.item_type === 'text' ? 'Text item' : 'Unknown source')}</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-xs text-[#8A93A6]">Start</label>
          <p className="text-sm text-white" data-testid="inspector-start-readout">{fmtTimePrecise(item.timeline_start_seconds)}</p>
        </div>
        <div className="space-y-1.5">
          <label className="text-xs text-[#8A93A6]">Duration</label>
          <p className="text-sm text-white" data-testid="inspector-duration-readout">{fmtTimePrecise(item.duration_seconds)}</p>
        </div>
      </div>

      {item.item_type !== 'text' && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="text-xs text-[#8A93A6]">Trim In (s)</label>
            <Input
              type="number" step="0.1" value={item.source_in_seconds.toFixed(1)}
              onChange={(e) => onUpdateItem(item.id, { source_in_seconds: Math.max(0, Number(e.target.value)) })}
              className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="inspector-trim-in-input"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-[#8A93A6]">Trim Out (s)</label>
            <Input
              type="number" step="0.1" value={item.source_out_seconds.toFixed(1)}
              onChange={(e) => onUpdateItem(item.id, { source_out_seconds: Math.max(item.source_in_seconds + 0.2, Number(e.target.value)) })}
              className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="inspector-trim-out-input"
            />
          </div>
        </div>
      )}

      {item.item_type === 'video' && (
        <>
          <EditorVideoControls
            item={item}
            onTransformChange={onTransformChange}
            onCropChange={onCropChange}
            onSpeedChange={onSpeedChange}
            onTogglePip={onTogglePip}
            onToggleReframing={onSetReframing}
          />
          <EditorKeyframeControls item={item} localTime={localTime} onAddAtPlayhead={onAddKeyframe} onRemove={onRemoveKeyframe} />
          <EditorTransitionsPanel
            item={item}
            onTransitionChange={(partial) => onUpdateItem(item.id, { transition_in: { ...(item.transition_in || { type: 'cut', duration_seconds: 0.5, direction: null }), ...partial } })}
            onRemoveTransition={() => onUpdateItem(item.id, { transition_in: null })}
          />
          <EditorColorControls item={item} onColorChange={(partial) => onUpdateItem(item.id, { color: { ...(item.color || {}), ...partial } })} />
          <EditorEffectsPanel
            item={item} localTime={localTime}
            onEffectsChange={(effects) => onUpdateItem(item.id, { effects })}
            onBlurRegionsChange={(regions) => onUpdateItem(item.id, { blur_regions: regions })}
          />
        </>
      )}

      {item.item_type === 'text' && (
        <>
          <div className="flex items-center justify-between pt-2 border-t border-white/5">
            <span className="text-xs text-[#555E6E] uppercase tracking-wide flex items-center gap-1.5">
              {isCaptionBlock ? <Captions className="h-3.5 w-3.5" /> : <TypeIcon className="h-3.5 w-3.5" />}
              {isCaptionBlock ? 'Caption Block' : 'Text Title'}
            </span>
            <Button size="sm" variant="outline" className="h-6 text-[10px] border-white/10" onClick={toggleTextMode} data-testid="text-mode-toggle-button">
              Switch to {isCaptionBlock ? 'Title' : 'Captions'}
            </Button>
          </div>
          {isCaptionBlock ? (
            <EditorCaptionControls
              item={item} localTime={localTime} siblingSources={siblingSources} masterComposition={masterComposition}
              onRequestTranscription={onRequestTranscription}
              onSegmentsChange={(segments) => setText({ segments })}
              onTextChange={setText}
            />
          ) : (
            <EditorTextControls
              item={item} presets={textStylePresets}
              onTextChange={setText}
              onTransformChange={(partial) => onUpdateItem(item.id, { transform: { ...(item.transform || { scale_percent: 100, position_x: 0, position_y: 0, rotation_degrees: 0, opacity_percent: 100 }), ...partial } })}
              onDuplicate={onDuplicateItem}
              onSaveAsPreset={(name) => onSaveTextPreset?.(name)}
              onApplyPreset={applyPreset}
              showSafeZones={showSafeZones}
              onToggleSafeZones={onToggleSafeZones}
            />
          )}
        </>
      )}

      {(item.item_type === 'video' || item.item_type === 'audio') && (
        <EditorAudioControls
          item={item} track={track} siblingSources={siblingSources} allTracks={allTracks}
          onAudioChange={onAudioChange} onDetachAudio={onDetachAudio} onReplaceAudioSource={onReplaceAudioSource}
          onAudioProcessingChange={onAudioProcessingChange} onDuckingChange={onDuckingChange}
        />
      )}

      <div className="space-y-2 pt-2 border-t border-white/5">
        <p className="text-xs text-[#555E6E] uppercase tracking-wide mb-1">Coming soon</p>
        <ComingSoonRow icon={Wand2} label="AI Tools" />
      </div>
    </div>
  );
}
